import React, { useState, useEffect } from 'react';
import LookPreviewCanvas from '../shared/LookPreviewCanvas';
import { fetchPresignedUrl } from '../shared/api';

const BuyAllPreviewSlot = ({ buyAllS3Path, width, height }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!buyAllS3Path) { setUrl(null); return; }
    fetchPresignedUrl(buyAllS3Path).then(setUrl).catch(() => setUrl(null));
  }, [buyAllS3Path]);

  return (
    <div style={{
      width, height,
      background: '#f3e5f5',
      border: '1px solid #ccc', borderRadius: 3,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden'
    }}>
      {url ? (
        <img src={url} alt="Buy All" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      ) : (
        <span style={{ fontSize: 9, color: '#9c27b0' }}>Buy All</span>
      )}
    </div>
  );
};

const FavoritesPreview = ({ slots, looks, buyAllS3Path, referencePoses, onClose }) => {
  const rows = [0, 1, 2, 3].map(rowNum =>
    slots.filter(s => s.row_number === rowNum)
  );
  const activePoses = (referencePoses || [])
    .map((pose, index) => ({ pose, index }))
    .filter(p => p.pose);

  // Count how many assigned looks use each reference pose
  // A look uses shot N if source_if_3, source_if_2, or source_if_1 equals "N" (shot number = pose index + 1)
  const assignedLooks = slots
    .filter(s => s.look_template_id && s.slot_type !== 'buy_all')
    .map(s => looks.find(l => l.id === s.look_template_id))
    .filter(Boolean);

  const poseUsageCounts = activePoses.map(({ index }) => {
    const shotNum = String(index + 1);
    return assignedLooks.filter(look =>
      look.source_if_3 === shotNum || look.source_if_2 === shotNum || look.source_if_1 === shotNum ||
      String(look.source_if_3) === shotNum || String(look.source_if_2) === shotNum || String(look.source_if_1) === shotNum
    ).length;
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,.6)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          maxWidth: 1100, width: '95%', maxHeight: '90vh', overflow: 'auto'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Favorites Layout Preview</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', fontSize: 20,
              cursor: 'pointer', color: '#999'
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          {/* Main layout grid */}
          <div style={{ flex: 1, background: '#f5f5f5', padding: 12, borderRadius: 4 }}>
            {rows.map((rowSlots, rowIdx) => (
              <div key={rowIdx} style={{
                display: 'flex', gap: 8, marginBottom: 8, justifyContent: 'center'
              }}>
                {rowSlots.map(slot => {
                  if (slot.slot_type === 'buy_all') {
                    return (
                      <BuyAllPreviewSlot
                        key={slot.slot_id}
                        buyAllS3Path={buyAllS3Path}
                        width={130}
                        height={162}
                      />
                    );
                  }

                  const look = looks.find(l => l.id === slot.look_template_id);
                  const isLandscape = slot.slot_type === 'landscape';
                  const w = isLandscape ? 240 : 130;
                  const h = isLandscape ? 192 : 162;
                  const hasBg = !!slot.background_image_url;

                  return (
                    <div
                      key={slot.slot_id}
                      style={{
                        width: w, height: h,
                        background: hasBg
                          ? undefined
                          : (look ? '#e8e8e8' : '#ddd'),
                        backgroundImage: hasBg ? `url(${slot.background_image_url})` : undefined,
                        backgroundSize: hasBg ? 'cover' : undefined,
                        backgroundPosition: hasBg ? 'center' : undefined,
                        border: '1px solid #ccc', borderRadius: 3,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden'
                      }}
                    >
                      {look ? (
                        <LookPreviewCanvas
                          look={look}
                          width={w}
                          height={h}
                          transparent
                          allLooks={looks}
                        />
                      ) : (
                        <span style={{ fontSize: 9, color: '#aaa' }}>{slot.slot_id}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Reference poses sidebar */}
          {activePoses.length > 0 && (
            <div style={{
              width: 120, flexShrink: 0,
              display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center'
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Reference Poses</div>
              {activePoses.map(({ pose, index }, vi) => {
                const useCount = poseUsageCounts[vi] || 0;
                return (
                  <div key={index} style={{ textAlign: 'center' }}>
                    <img
                      src={pose}
                      alt={`Shot ${index + 1}`}
                      style={{
                        width: 100, height: 150, objectFit: 'contain',
                        borderRadius: 4, border: '1px solid #ddd', display: 'block',
                        background: '#f0f0f0'
                      }}
                    />
                    <div style={{ fontSize: 11, color: '#666', marginTop: 3, fontWeight: 500 }}>
                      Shot {index + 1}
                    </div>
                    <div style={{
                      fontSize: 10, marginTop: 1,
                      color: useCount > 0 ? '#1976d2' : '#bbb',
                      fontWeight: useCount > 0 ? 500 : 400
                    }}>
                      {useCount} {useCount === 1 ? 'use' : 'uses'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: '#999', textAlign: 'center' }}>
          {slots.filter(s => s.look_template_id && s.slot_type !== 'buy_all').length} of 11 look slots assigned
          <span style={{ marginLeft: 12, color: '#bbb' }}>
            Variable tokens (e.g. {'{{first_name}}'}) shown as placeholders
          </span>
        </div>
      </div>
    </div>
  );
};

export default FavoritesPreview;
